import * as React from 'react';
import classnames from 'classnames';
import {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';

import {User} from "features/authentication/models/User";
import {useModal} from "features/modal/hooks/useModal";
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {EpiImage, SearchResult} from "features/search/types";
import {EpiVideo, GalleryType} from "features/template/types";
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import {gtmEventsGalleryOpen, gtmEventsGalleryViewCount} from 'features/services/googleTagManager/events';

import './style.scss';

import {Icon} from 'components/atoms/Media/Icon';
import {UserAgent} from 'components/atoms/UserAgent';
import {PlayPause} from 'components/atoms/LinksAndButtons/PlayPause';
import {FavoriteButton} from "@atoms/LinksAndButtons/FavoriteButton";
import {AsyncImage} from 'components/atoms/Media/AsyncImage';
import {MovingStill} from 'components/atoms/Media/MovingStill';
import {ResidenceTags} from 'components/atoms/Tags/ResidenceTags';
import {ModalGallery} from 'components/modals/General/ModalGallery';
import {Button, ButtonColor, ButtonSize, ButtonType} from 'components/atoms/LinksAndButtons/Button';

interface Props {
    title: string;
    children?: React.ReactNode;
    digitalShowingText?: string;
    digitalShowingLinkUrl?: string;

    galleryType: GalleryType;
    heroImage?: EpiImage;
    images?: EpiImage[];

    heroVideo?: EpiVideo;
    heroVideoImage?: EpiImage;
    video?: boolean;
    demo?: boolean;

    concept?: string;
    stageStatus?: string;
    objectStatus?: string;
    rentalStatus?: string;
    residence?: SearchResult;

    isMobile: boolean;
    showFavorite?: boolean;
    margin?: boolean;

    playVideoOnClick?: boolean;
    user?: User | null;
}

export const ImageHeader: React.FunctionComponent<Props> = ({
    title, children, digitalShowingText, digitalShowingLinkUrl,
    galleryType, heroImage, images,
    heroVideo, heroVideoImage,
    concept, stageStatus, objectStatus, rentalStatus, residence,
    isMobile, showFavorite, margin,
    playVideoOnClick
}) => {
    const { open } = useModal();

    // For gtm
    let navigationCount: number = 1;
    const [playing, setPlaying] = useState<boolean>(true);

    const onSlide = (_event: unknown) => {
        navigationCount++;
        return;
    };
    const playButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setPlaying(!playing);
    };

    const shouldShowMovingStill = () => {
        if (heroVideo) {
            return heroVideo.url;
        }
        return undefined;
    };

    const onImageClick = () => {
        if (images === null || typeof images === 'undefined') return;

        gtmEventsGalleryOpen(galleryType)();
        gtmEventsGalleryOpen("mobilebutton")();

        open(
            <ModalGallery
                images={images}
                startIndex={0}
                onSlide={onSlide}
                videoUrl={digitalShowingLinkUrl}
                videoDescription={digitalShowingText}
                playVideoOnClick={playVideoOnClick}
                movingStill={shouldShowMovingStill()}
                movingStillCover={heroVideoImage ? heroVideoImage : heroImage}
            />,
            {fullscreen: true, ariaLabel: getFormattedMessage('jm.components.modalGallery.ariaLabel'), closeCallback: () => {
                if (images && images.length > 0) {
                    const count = navigationCount;
                    navigationCount = 1;

                    let precentage = Math.round((count / images.length) * 10) * 10;

                    if(precentage < 1) {
                        precentage = 1;
                    }

                    if(precentage > 100) {
                        precentage = 100;
                    }

                    gtmEventsGalleryViewCount(galleryType, parseInt(precentage.toString(), 10).toString())();
                }
            }}
        );
    };

    const hasGallery = () => !(images === null || typeof images === 'undefined');

    const [galleryButton, setGalleryButton] = useState<boolean>(false);
    const showGalleryButton = () => {
        if(!isMobile) {
            if (heroVideo && heroVideo !== null) {
                if (digitalShowingLinkUrl && digitalShowingLinkUrl !== null) {
                    setGalleryButton(true); //video, digital showing
                } else {
                    setGalleryButton(images !== null && typeof images != 'undefined' && images.length > 0); //video, no digital showing
                }
            } else {
                if (digitalShowingLinkUrl && digitalShowingLinkUrl !== null) {
                    setGalleryButton(true); //no video, digital showing
                } else {
                    setGalleryButton(images !== null && typeof images != 'undefined' && images.length > 1); //no video, no digital showing
                }
            }
        }
    };
    useEffect(() => {
        showGalleryButton();
    }, []);

    const headerClasses = classnames('image-header', {
        'image-header--has-gallery': hasGallery(),
        'image-header--with-margin': margin,
    });

    const overlayClasses = classnames('image-header__buttons', {
        'image-header__buttons--overlay': galleryButton
    });

    return (
        <div
            className={headerClasses} 
            onClick={onImageClick} 
        >
            {(!heroVideo && heroImage) &&
                <div className="image-header__top-container">
                    <div className="image-header__img-wrapper">
                        <AsyncImage
                            image={heroImage}
                            headerImage
                            quality={ImageSizes.SIZE_2560}
                            alt={title}
                        />
                    </div>
                </div>
            }
            {heroVideo &&
                <div className="image-header__top-container" >
                    <div className="async-image__aspect-holder" />
                    <div className="image-header__video-container">
                        <MovingStill
                            srcset={[{src: heroVideo.url, type: "video/mp4"}]}
                            fullWidth
                            demo={false}
                            withSound={false}
                            poster={heroImage?.url}
                            playing={playing}
                        />
                    </div>
                </div>
            }

            <div className={overlayClasses}>
                {((showFavorite && residence) || heroVideo) &&
                    // Favorit- och paus-knappen på samma rad, hjärtat ytterst (AB#76295).
                    <div className="image-header__buttons__actions">
                        {showFavorite && residence &&
                            <FavoriteButton residence={residence} />
                        }
                        {heroVideo &&
                            <PlayPause
                                playing={playing}
                                small
                                onClick={playButtonClick}
                            />
                        }
                    </div>
                }
                {stageStatus &&
                    <UserAgent mobile tablet>
                        <ResidenceTags concept={concept} stageStatus={stageStatus}/>
                    </UserAgent>
                }
                {objectStatus &&
                    <UserAgent mobile tablet>
                        <ResidenceTags concept={concept} objectStatus={objectStatus}/>
                    </UserAgent>
                }
                {rentalStatus &&
                    <UserAgent mobile tablet>
                        <ResidenceTags rentalStatus={rentalStatus}/>
                    </UserAgent>
                }
                {galleryButton &&
                    <Button 
                        icon={<Icon name="images" noStroke/>}
                        className="image-header__buttons__gallery" 
                        onClick={onImageClick} 
                        buttonColor={ButtonColor.LIGHT} 
                        buttonSize={ButtonSize.DEFAULT} 
                        buttonType={ButtonType.DEFAULT} 
                    >
                        {digitalShowingLinkUrl
                            ? <FormattedMessage id="jm.components.ImageHeader.digitalShowing"/>
                            : <FormattedMessage id="jm.components.ImageHeader.seeImages"/>
                        }
                    </Button>
                }
            </div>

            {children &&
                <div className="image-header__content dark-bg">
                    {children}
                </div>
            }
        </div>
    )
};
